import React from 'react';
import { IconButton, Tooltip, Box, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';



const PlaybackControls = ({
  audioState,
  controlHandlers,
  layout,
  segments = [],
  onPreviousCard,
  onNextCard,
  onCardFocusChange
}) => {
  const buttonSizes = layout.buttonSizes;
  const iconSize = layout.buttonSizes.iconSize;

  // Normalize & sort segments with optimized memoization
  const normSegments = React.useMemo(() => {
    if (!Array.isArray(segments) || segments.length === 0) return [];
    
    // Check if segments are already sorted by start time
    let isSorted = true;
    let lastStart = -Infinity;
    
    for (const segment of segments) {
      const start = Number(segment.start);
      if (!Number.isFinite(start) || !Number.isFinite(Number(segment.end))) {
        continue; // Skip invalid segments
      }
      if (start < lastStart) {
        isSorted = false;
        break;
      }
      lastStart = start;
    }
    
    const normalizedSegments = segments
      .map(s => ({ ...s, start: Number(s.start), end: Number(s.end) }))
      .filter(s => Number.isFinite(s.start) && Number.isFinite(s.end));
    
    // Only sort if not already sorted
    return isSorted ? normalizedSegments : normalizedSegments.sort((a, b) => a.start - b.start);
  }, [segments]);

  // ---- helpers: optimized segment finding with binary search for large arrays ----
  const findNextIndexStrict = React.useCallback((t) => {
    // first segment that starts strictly after t+1
    const targetTime = t + 1;
    
    // Use binary search for better performance with large segment arrays
    if (normSegments.length > 50) {
      let left = 0, right = normSegments.length - 1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (normSegments[mid].start > targetTime) {
          if (mid === 0 || normSegments[mid - 1].start <= targetTime) {
            return mid;
          }
          right = mid - 1;
        } else {
          left = mid + 1;
        }
      }
      return -1;
    }
    
    // Linear search for smaller arrays
    for (let i = 0; i < normSegments.length; i++) {
      if (normSegments[i].start > targetTime) return i;
    }
    return -1;
  }, [normSegments]);

  const findPrevIndexStrict = React.useCallback((t) => {
    // last segment that starts strictly before t
    if (normSegments.length > 50) {
      let left = 0, right = normSegments.length - 1, result = -1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        if (normSegments[mid].start < t) {
          result = mid;
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      return result;
    }
    
    // Linear search for smaller arrays
    let idx = -1;
    for (let i = 0; i < normSegments.length; i++) {
      if (normSegments[i].start < t) idx = i;
      else break;
    }
    return idx;
  }, [normSegments]);

  const findInsideIndex = React.useCallback((t) => {
    // Linear search is typically fast enough for finding current segment
    for (let i = 0; i < normSegments.length; i++) {
      const s = normSegments[i];
      if (s.start <= t && t < s.end) return i;
    }
    return -1;
  }, [normSegments]);

  const seekTo = React.useCallback((t) => {
    controlHandlers.seekToTime?.(t);
    
    // Trigger card focus change to scroll to the appropriate card
    if (onCardFocusChange) {
      onCardFocusChange(t);
    }
  }, [controlHandlers, onCardFocusChange]);

  // ---- NEXT: strict rule ----
  const handleNextCard = React.useCallback(() => {
    if (normSegments.length === 0) {
      onNextCard?.();
      return;
    }
    const t = Number(audioState.currentTime) || 0;
    const nextIdx = findNextIndexStrict(t);
    if (nextIdx === -1) {
      onNextCard?.();
      return;
    }
    seekTo(normSegments[nextIdx].start);
  }, [normSegments, audioState.currentTime, findNextIndexStrict, seekTo, onNextCard]);

  // ---- PREVIOUS: minimal + standard UX ----
  const handlePreviousCard = React.useCallback(() => {
    if (normSegments.length === 0) {
      onPreviousCard?.();
      return;
    }
    const t = Number(audioState.currentTime) || 0;

    // Option A (standard UX): if >2s into current segment, restart it, else go to previous segment
    const insideIdx = findInsideIndex(t);
    if (insideIdx >= 0) {
      const seg = normSegments[insideIdx];
      if (t - seg.start > 2) { seekTo(seg.start); return; }
      const prevIdx = insideIdx - 1;
      if (prevIdx < 0) { onPreviousCard?.(); return; }
      seekTo(normSegments[prevIdx].start);
      return;
    }

    // In a gap: go to the last segment strictly before t (or previous card if none)
    const prevIdx = findPrevIndexStrict(t);
    if (prevIdx < 0) { onPreviousCard?.(); return; }
    seekTo(normSegments[prevIdx].start);

    /* Option B (strict previous): uncomment to always use strict rule
    const prevIdxStrict = findPrevIndexStrict(t);
    if (prevIdxStrict < 0) { onPreviousCard?.(); return; }
    seekTo(normSegments[prevIdxStrict].start);
    */
  }, [normSegments, audioState.currentTime, findInsideIndex, findPrevIndexStrict, seekTo, onPreviousCard]);

  // Memoize disabled states to prevent unnecessary re-renders
  const prevDisabled = React.useMemo(() => 
    normSegments.length === 0 && !onPreviousCard, 
    [normSegments.length, onPreviousCard]
  );
  const nextDisabled = React.useMemo(() => 
    normSegments.length === 0 && !onNextCard, 
    [normSegments.length, onNextCard]
  );

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
      {/* Previous */}
      <Tooltip title="Previous card">
        <IconButton
          aria-label="Previous card"
          onClick={handlePreviousCard}
          disabled={prevDisabled}
          size="small"
          sx={{
            width: buttonSizes.skip, height: buttonSizes.skip, 
            color: 'var(--audioControlButtonColor)',
            '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)', color: 'var(--audioControlButtonHoverColor)' },
            '&:disabled': { color: 'grey.300' },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <SkipPreviousIcon fontSize={iconSize} />
        </IconButton>
      </Tooltip>

      {/* Skip Backward */}
      <Tooltip title="Skip backward 5 seconds">
        <IconButton
          aria-label="Skip backward 5 seconds"
          onClick={controlHandlers.handleSkipBackward}
          disabled={audioState.isLoading || !!audioState.error}
          size="small"
          sx={{
            width: buttonSizes.skip, height: buttonSizes.skip, 
            color: 'var(--audioControlButtonColor)',
            '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)', color: 'var(--audioControlButtonHoverColor)' },
            '&:disabled': { color: 'grey.300' },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1, color: 'inherit' }}>
            -5s
          </Typography>
        </IconButton>
      </Tooltip>

      {/* Play/Pause */}
      <IconButton
        aria-label={audioState.isPlaying ? 'Pause' : 'Play'}
        onClick={controlHandlers.handlePlayPause}
        disabled={audioState.isLoading || !!audioState.error}
        size="small"
        sx={{
          width: buttonSizes.play, height: buttonSizes.play,
          backgroundColor: 'var(--playButtonColor)',
          color: 'white',
          '&:hover': { backgroundColor: 'var(--pauseButtonColor)' },
          '&:disabled': { backgroundColor: 'grey.300' },
        }}
      >
        {audioState.isLoading ? (
          <Box
            sx={{
              width: buttonSizes.play * 0.5, 
              height: buttonSizes.play * 0.5, 
              border: '2px solid', 
              borderColor: 'white', 
              borderTopColor: 'transparent',
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexShrink: 0, // Prevent shrinking that could cause oval shape
              '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
            }}
          />
        ) : audioState.isPlaying ? <PauseIcon fontSize={iconSize} /> : <PlayArrowIcon fontSize={iconSize} />}
      </IconButton>

      {/* Skip Forward */}
      <Tooltip title="Skip forward 5 seconds">
        <IconButton
          aria-label="Skip forward 5 seconds"
          onClick={controlHandlers.handleSkipForward}
          disabled={audioState.isLoading || !!audioState.error}
          size="small"
          sx={{
            width: buttonSizes.skip, height: buttonSizes.skip, 
            color: 'var(--audioControlButtonColor)',
            '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)', color: 'var(--audioControlButtonHoverColor)' },
            '&:disabled': { color: 'grey.300' },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1, color: 'inherit' }}>
            +5s
          </Typography>
        </IconButton>
      </Tooltip>

      {/* Next (strict) */}
      <Tooltip title="Next card">
        <IconButton
          aria-label="Next card"
          onClick={handleNextCard}
          disabled={nextDisabled}
          size="small"
          sx={{
            width: buttonSizes.skip, height: buttonSizes.skip, 
            color: 'var(--audioControlButtonColor)',
            '&:hover': { backgroundColor: 'var(--toolbarButtonHoverColor)', color: 'var(--audioControlButtonHoverColor)' },
            '&:disabled': { color: 'grey.300' },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <SkipNextIcon fontSize={iconSize} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export default PlaybackControls;
